import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { API } from "../config/api";
import {
  Calendar as CalendarIcon, Download, FileSpreadsheet, FileText,
  ChevronLeft, ChevronRight, Clock, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import {
  LineChart, Line, ComposedChart, Bar, Area, AreaChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from "recharts";
import IntradayPatternsChart from "./IntradayPatternsChart";
import useTodayVwap from "../hooks/useTodayVwap";
import { enrichWithAnchoredVwap } from "../utils/vwap";

/**
 * "60 Day History" tab — pick any of the last ~60 trading days and see
 * pre-market data, day OHLC, support/resistance, 78 × 5-min candles,
 * regression line, and download Excel / PDF.
 */
export default function SixtyDayHistory({ ticker }) {
  const [calendar, setCalendar] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  // Today's VWAP for the selected historical day (or live if today selected)
  const vwap = useTodayVwap(ticker, selectedDate);

  // Refs for html2canvas capture of each rendered chart container
  const priceChartRef = useRef(null);
  const candleChartRef = useRef(null);
  const pctChartRef = useRef(null);
  const summaryRef = useRef(null);

  // Load available trading dates
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/stock/historical-day/${ticker}/calendar`, { timeout: 30000 });
        if (cancelled) return;
        setCalendar(res.data);
        // Default to most recent date
        if (res.data?.dates?.length) {
          setSelectedDate(res.data.dates[res.data.dates.length - 1]);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load calendar");
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  // Load day data whenever date changes
  useEffect(() => {
    if (!ticker || !selectedDate) return;
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const res = await axios.get(`${API}/stock/historical-day/${ticker}?date=${selectedDate}`, { timeout: 30000 });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load day data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, selectedDate]);

  const dates = calendar?.dates || [];
  const currentIdx = dates.indexOf(selectedDate);
  const canPrev = currentIdx > 0;
  const canNext = currentIdx >= 0 && currentIdx < dates.length - 1;
  const gotoPrev = () => canPrev && setSelectedDate(dates[currentIdx - 1]);
  const gotoNext = () => canNext && setSelectedDate(dates[currentIdx + 1]);

  const download = async (format) => {
    if (!selectedDate) return;
    setDownloading(format);
    try {
      if (format === "xlsx") {
        // Excel still generated server-side (raw bar tables)
        const res = await axios.get(
          `${API}/stock/historical-day/${ticker}/export?date=${selectedDate}&format=xlsx`,
          { responseType: "blob", timeout: 60000 }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${ticker}_${selectedDate}_60day_history.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else if (format === "pdf") {
        // Snapshot the ACTUAL rendered charts via html2canvas → embed in jsPDF
        await buildPdfFromDom();
      }
    } catch (e) {
      alert(`Download failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const buildPdfFromDom = async () => {
    const bg = "#0a0a0a";
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 36;
    let y = margin;

    // Header
    pdf.setFillColor(15, 23, 42);
    pdf.rect(0, 0, pageWidth, 54, "F");
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text(`${ticker} — ${selectedDate}`, margin, 30);
    pdf.setFontSize(11);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(156, 163, 175);
    pdf.text("60-Day History Report", margin, 46);
    y = 70;

    // Stats table (key numbers)
    const reg = data?.summary?.regular || {};
    const pre = data?.summary?.premarket || {};
    const change$ = data?.summary?.change_dollars;
    const changePct = data?.summary?.change_pct;
    pdf.setTextColor(30, 41, 59);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    const col1 = margin;
    const col2 = margin + 110;
    const col3 = margin + 270;
    const col4 = margin + 380;
    const rows = [
      ["Open", fmt$(reg.open), "High (R)", fmt$(reg.high)],
      ["Low (S)", fmt$(reg.low), "Close", fmt$(reg.close)],
      ["Prior Close", fmt$(data?.summary?.prior_close), "Day Change",
        change$ != null ? `${change$ >= 0 ? "+" : ""}$${change$.toFixed(2)} (${changePct?.toFixed(2)}%)` : "—"],
      ["PM Open", fmt$(pre.open), "PM High", fmt$(pre.high)],
      ["PM Low", fmt$(pre.low), "PM Last", fmt$(pre.close)],
      ["Volume", reg.volume ? reg.volume.toLocaleString() : "—", "Bars", String(reg.bar_count || 0)],
    ];
    for (const [a, b, c, d] of rows) {
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(67, 56, 202);
      pdf.text(a, col1, y);
      pdf.text(c, col3, y);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(30, 41, 59);
      pdf.text(String(b ?? "—"), col2, y);
      pdf.text(String(d ?? "—"), col4, y);
      y += 16;
    }
    y += 10;

    // Capture each chart
    const charts = [
      { ref: priceChartRef, title: "Price Action + Support/Resistance + Regression" },
      { ref: candleChartRef, title: "78 × 5-min Candlestick" },
      { ref: pctChartRef, title: "Intraday % from Open" },
    ];
    for (const { ref, title } of charts) {
      if (!ref.current) continue;
      try {
        const canvas = await html2canvas(ref.current, {
          backgroundColor: bg,
          scale: 1.5,
          useCORS: true,
          logging: false,
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.85);
        const imgW = pageWidth - margin * 2;
        const imgH = (canvas.height * imgW) / canvas.width;

        // Page break if the image won't fit
        if (y + imgH + 30 > pageHeight) {
          pdf.addPage();
          y = margin;
        }
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(67, 56, 202);
        pdf.text(title, margin, y);
        y += 10;
        pdf.addImage(imgData, "JPEG", margin, y, imgW, imgH);
        y += imgH + 16;
      } catch (err) {
        console.error(`Failed to snapshot ${title}:`, err);
      }
    }

    pdf.save(`${ticker}_${selectedDate}_60day_history.pdf`);
  };

  const s = data?.summary || {};
  const reg = s.regular || {};
  const pre = s.premarket || {};
  const post = s.postmarket || {};
  const change$ = s.change_dollars;
  const changePct = s.change_pct;
  const positive = (change$ ?? 0) > 0;
  const negative = (change$ ?? 0) < 0;

  const candles = data?.regular_candles || [];

  // Linear regression line data for chart overlay
  const regressionData = useMemo(() => {
    if (!data?.regression || !candles.length) return null;
    const { slope_per_bar, intercept } = data.regression;
    return candles.map((c, i) => ({
      time: c.time,
      reg: slope_per_bar * i + intercept,
    }));
  }, [candles, data]);

  return (
    <div className="space-y-3" data-testid="sixty-day-history-tab">
      {/* Header: date picker + download buttons — placed FIRST so it's easy to find */}
      <div className="flex items-center justify-between flex-wrap gap-3 bg-[#0d0d0d] border border-gray-800 rounded p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarIcon className="w-4 h-4 text-indigo-400" />
          <span className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Select trading day</span>
          <button
            onClick={gotoPrev}
            disabled={!canPrev}
            data-testid="history-prev-day"
            className="p-1 rounded border border-gray-700 bg-gray-900 hover:bg-gray-800 disabled:opacity-40"
            title="Previous trading day"
          ><ChevronLeft className="w-4 h-4 text-gray-400" /></button>
          <input
            type="date"
            value={selectedDate || ""}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={calendar?.earliest}
            max={calendar?.latest}
            list="history-available-dates"
            data-testid="history-date-input"
            className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white font-mono focus:outline-none focus:border-indigo-500"
          />
          <datalist id="history-available-dates">
            {dates.map((d) => <option key={d} value={d} />)}
          </datalist>
          <button
            onClick={gotoNext}
            disabled={!canNext}
            data-testid="history-next-day"
            className="p-1 rounded border border-gray-700 bg-gray-900 hover:bg-gray-800 disabled:opacity-40"
            title="Next trading day"
          ><ChevronRight className="w-4 h-4 text-gray-400" /></button>
          {calendar?.count && (
            <span className="text-[10px] text-gray-500 font-mono">{calendar.count} days available</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => download("xlsx")}
            disabled={!data || !!downloading}
            data-testid="history-download-xlsx"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-green-800/60 bg-green-950/30 hover:bg-green-900/40 text-green-300 disabled:opacity-40"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            {downloading === "xlsx" ? "Building…" : "Excel"}
          </button>
          <button
            onClick={() => download("pdf")}
            disabled={!data || !!downloading}
            data-testid="history-download-pdf"
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded border border-red-800/60 bg-red-950/30 hover:bg-red-900/40 text-red-300 disabled:opacity-40"
          >
            <FileText className="w-3.5 h-3.5" />
            {downloading === "pdf" ? "Building…" : "PDF"}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-2 text-xs rounded border border-red-900/60 bg-red-950/30 text-red-300" data-testid="history-error">{error}</div>
      )}

      {/* Intraday Time-of-Day Patterns chart — driven by the date picker above.
          When a historical day is picked, the green/red 60-day pattern context,
          today's-price line, VWAP, regression, and oscillator all reflect that
          day. */}
      <div data-testid="history-intraday-patterns-chart">
        <IntradayPatternsChart ticker={ticker} date={selectedDate || undefined} />
      </div>

      {loading && (
        <div className="py-6 text-center text-gray-500 text-sm">Loading {selectedDate}…</div>
      )}

      {data && !loading && (
        <>
          {/* Summary grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="history-summary-grid">
            <StatTile label="Open" value={fmt$(reg.open)} />
            <StatTile label="High (Resistance)" value={fmt$(reg.high)} color="text-red-300" />
            <StatTile label="Low (Support)" value={fmt$(reg.low)} color="text-green-300" />
            <StatTile label="Close" value={fmt$(reg.close)} highlight />
            <StatTile
              label="Day Change"
              value={change$ !== null ? `${change$ > 0 ? "+" : ""}$${change$?.toFixed?.(2)}` : "—"}
              color={positive ? "text-green-300" : negative ? "text-red-300" : "text-gray-300"}
              icon={positive ? <TrendingUp className="w-3 h-3" /> : negative ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
            />
            <StatTile
              label="Day %"
              value={changePct !== null ? `${changePct > 0 ? "+" : ""}${changePct?.toFixed?.(2)}%` : "—"}
              color={positive ? "text-green-300" : negative ? "text-red-300" : "text-gray-300"}
            />
          </div>

          {/* Pre-market */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-indigo-950/20 border border-indigo-900/40 rounded p-2" data-testid="history-premarket">
            <div className="col-span-2 md:col-span-1 flex items-center gap-1 text-[11px] uppercase tracking-wide text-indigo-300 font-semibold">
              <Clock className="w-3 h-3" /> Pre-Market
            </div>
            <SmallStat label="Open" value={fmt$(pre.open)} />
            <SmallStat label="High" value={fmt$(pre.high)} />
            <SmallStat label="Low" value={fmt$(pre.low)} />
            <SmallStat label="Last" value={fmt$(pre.close)} />
          </div>

          {/* After-hours */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-purple-950/20 border border-purple-900/40 rounded p-2" data-testid="history-postmarket">
            <div className="col-span-2 md:col-span-1 flex items-center gap-1 text-[11px] uppercase tracking-wide text-purple-300 font-semibold">
              <Clock className="w-3 h-3" /> After-Hours
            </div>
            <SmallStat label="Open" value={fmt$(post.open)} />
            <SmallStat label="High" value={fmt$(post.high)} />
            <SmallStat label="Low" value={fmt$(post.low)} />
            <SmallStat label="Last" value={fmt$(post.close)} />
          </div>

          {/* Chart 1: Price line + S/R + regression */}
          <ChartWrapper innerRef={priceChartRef} title={`Price Action  ·  78 × 5-min bars`} testid="history-price-chart">
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={enrichWithAnchoredVwap(candles.map((c, i) => ({ ...c, reg: regressionData?.[i]?.reg })))} margin={{ top: 10, right: 50, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} interval={Math.max(1, Math.floor(candles.length / 10))} />
                <YAxis stroke="#9ca3af" fontSize={10} domain={["auto", "auto"]} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip content={<PriceTip />} />
                {s.resistance && <ReferenceLine y={s.resistance} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `R: $${s.resistance}`, fill: "#ef4444", fontSize: 10, position: "right" }} />}
                {s.support && <ReferenceLine y={s.support} stroke="#22c55e" strokeDasharray="5 5" label={{ value: `S: $${s.support}`, fill: "#22c55e", fontSize: 10, position: "right" }} />}
                <Line type="monotone" dataKey="close" stroke="#a855f7" strokeWidth={1.8} dot={false} name="Price" />
                {/* Anchored VWAP — violet per-bar curve, accumulates from market open */}
                <Line type="monotone" dataKey="vwap" stroke="#ee82ee" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls name="VWAP (anchored)" isAnimationActive={false} />
                {regressionData && <Line type="monotone" dataKey="reg" stroke="#06b6d4" strokeWidth={1.4} strokeDasharray="5 5" dot={false} name="Regression" />}
              </ComposedChart>
            </ResponsiveContainer>
          </ChartWrapper>

          {/* Chart 2: Candlestick (rendered as high-low wicks + body bars) */}
          <ChartWrapper innerRef={candleChartRef} title={`Candlestick  ·  ${candles.length} bars`} testid="history-candlestick-chart">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={candles} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} interval={Math.max(1, Math.floor(candles.length / 10))} />
                <YAxis stroke="#9ca3af" fontSize={10} domain={["auto", "auto"]} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                <Tooltip content={<CandleTip />} />
                {/* High-low wick */}
                <Bar dataKey={(d) => [d.low, d.high]} fill="transparent" shape={WickShape} isAnimationActive={false} />
                {/* Body */}
                <Bar dataKey={(d) => [Math.min(d.open ?? d.close, d.close), Math.max(d.open ?? d.close, d.close)]} shape={BodyShape} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartWrapper>

          {/* Chart 3: Intraday % from open with volume */}
          <ChartWrapper innerRef={pctChartRef} title="Intraday Performance (% from Open)" testid="history-pct-chart">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={candles} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                <defs>
                  <linearGradient id="pctPos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="pctNeg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.05} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0.45} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} interval={Math.max(1, Math.floor(candles.length / 10))} />
                <YAxis stroke="#9ca3af" fontSize={10} tickFormatter={(v) => `${v.toFixed(2)}%`} />
                <Tooltip content={<PctTip />} />
                <ReferenceLine y={0} stroke="#6b7280" strokeWidth={1} />
                <Area type="monotone" dataKey="pct_from_open" stroke="#ffffff" strokeWidth={1.5} fill="url(#pctPos)" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartWrapper>

          <p className="text-[10px] text-gray-500 leading-snug text-center">
            Data: Yahoo v8 (5-min bars, incl. pre/post). 78 candles = 6.5 hrs × 12 bars/hr. Exports embed rendered charts (PDF) or raw bar tables (Excel).
          </p>
        </>
      )}
    </div>
  );
}

const fmt$ = (n) => (n == null || isNaN(n) ? "—" : `$${Number(n).toFixed(2)}`);

function StatTile({ label, value, color = "text-white", icon, highlight }) {
  return (
    <div className={`rounded border ${highlight ? "border-indigo-700/60 bg-indigo-950/30" : "border-gray-800 bg-gray-900/60"} p-2`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
        {icon}{label}
      </div>
      <div className={`mt-0.5 font-mono text-sm font-bold ${color}`}>{value}</div>
    </div>
  );
}

function SmallStat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="font-mono text-xs text-gray-200 font-semibold">{value}</div>
    </div>
  );
}

function ChartWrapper({ title, children, testid, innerRef }) {
  return (
    <div ref={innerRef} className="rounded border border-gray-800 bg-[#0a0a0a] p-3 space-y-2" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{title}</div>
      {children}
    </div>
  );
}

// Recharts custom shapes for the candlestick body and wick
function BodyShape(props) {
  const { x, y, width, height, payload } = props;
  const up = (payload.close ?? 0) >= (payload.open ?? 0);
  const fill = up ? "#22c55e" : "#ef4444";
  const w = Math.max(width * 0.65, 1.5);
  const cx = x + width / 2 - w / 2;
  return <rect x={cx} y={y} width={w} height={Math.max(height, 1)} fill={fill} />;
}
function WickShape(props) {
  const { x, y, width, height, payload } = props;
  const up = (payload.close ?? 0) >= (payload.open ?? 0);
  const stroke = up ? "#22c55e" : "#ef4444";
  const cx = x + width / 2;
  return <line x1={cx} x2={cx} y1={y} y2={y + height} stroke={stroke} strokeWidth={1} />;
}

function PriceTip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-[11px] font-mono">
      <div className="text-gray-400">{p.time}</div>
      <div className="text-white font-bold">${p.close?.toFixed(2)}</div>
      <div className="text-gray-500 text-[10px]">Vol {p.volume?.toLocaleString?.()}</div>
    </div>
  );
}
function CandleTip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-[11px] font-mono">
      <div className="text-gray-400">{p.time}</div>
      <div className="text-white">O: ${p.open?.toFixed(2)} · C: ${p.close?.toFixed(2)}</div>
      <div className="text-gray-400">H: ${p.high?.toFixed(2)} · L: ${p.low?.toFixed(2)}</div>
    </div>
  );
}
function PctTip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-[11px] font-mono">
      <div className="text-gray-400">{p.time}</div>
      <div className="text-white">{p.pct_from_open?.toFixed?.(3)}%</div>
    </div>
  );
}
